import { useEffect, useMemo, useReducer, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Chess } from 'chess.js';
import type { Key } from 'chessground/types';
import { Board, type BoardMoveEvent } from '@/chess/board';
import {
  FixedNav,
  Caption,
  Button,
  AnimatedNumber,
  PulseDot,
  SessionTimer,
  XpFloater,
  KidHint,
  TurnIndicator,
  useMinDuration,
  type TurnIndicatorState,
  ENDGAME_SOURCE_KIND_LABEL_FR,
} from '@/ui';
import { useAwardXp } from '@/api/xp';
import { endgameApi, type EndgameAttemptPayload, type EndgameDetail } from './api';
import { endgameDrillReducer, initialEndgameDrillState } from './endgameDrillReducer';

/**
 * Endgame Drill — mode `play_against_engine` V2 (port proto + refacto T2 MVP2.5).
 *
 * State machine migrée vers `useReducer` immutable (cf. `endgameDrillReducer.ts`).
 * Le state ne porte plus d'instance `Chess` partagée — uniquement la FEN.
 * Le coup adversaire est dispatché via `useEffect(awaiting_engine)` +
 * `requestAnimationFrame` (résout le bug A2 tablette : `setTimeout` inline
 * était throttlé par iOS Safari pendant un touch event).
 *
 * Refonte visuelle = layout 2 colonnes (left: cursor + eval glass + objectif
 * + sequence chips + source + explanation post-resolve, right: Board + status).
 */

const OUTCOME_LABEL: Record<EndgameDetail['target_outcome'], string> = {
  win: 'Gagner',
  draw: 'Annuler',
  defend: 'Défendre',
};
const OUTCOME_COLOR: Record<EndgameDetail['target_outcome'], string> = {
  win: 'var(--amber-500)',
  draw: 'var(--ink-300)',
  defend: 'var(--paper-50)',
};

export function EndgameDrillPage(): JSX.Element {
  const { slug = '' } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const detailQ = useQuery({
    queryKey: ['endgame', slug],
    queryFn: () => endgameApi.detail(slug),
    enabled: slug !== '',
  });

  const [state, dispatch] = useReducer(endgameDrillReducer, initialEndgameDrillState);
  const [score, setScore] = useState<{ ok: number; ko: number }>({ ok: 0, ko: 0 });
  const [sessionXp, setSessionXp] = useState(0);
  const [xpBurst, setXpBurst] = useState<{ value: number; key: number } | null>(null);
  const sessionStartRef = useRef<number>(Date.now());
  const positionStartRef = useRef<number>(Date.now());
  const attemptStartRef = useRef<number>(performance.now());
  // Évite double-trigger des effets de bord (XP, score, log) sur StrictMode
  // double render. On enregistre la clé `${slug}:${cursor}` du dernier coup
  // dont les side-effects ont été déclenchés.
  const lastBoundaryRef = useRef<string | null>(null);

  // Barème XP : +15 par coup correct (drill long et technique, cf. docs/XP_BAREME.md).
  const XP_PER_MOVE = 15;

  // T5 MVP2.5 : persiste l'XP en backend (idempotence 1h sur (user, module,
  // source_ref)). source_ref = slug de la finale → on award sur le coup
  // final qui clôt la séquence (transition vers `success`).
  const awardXp = useAwardXp();

  function start(detail: EndgameDetail): void {
    attemptStartRef.current = performance.now();
    positionStartRef.current = Date.now();
    lastBoundaryRef.current = null;
    dispatch({ type: 'START_DRILL', detail });
  }

  async function logAttempt(
    detail: EndgameDetail,
    result: 'success' | 'fail',
    playedMove: string,
    expectedMove: string,
  ): Promise<void> {
    const payload: EndgameAttemptPayload = {
      module: 'endgame',
      slug: detail.slug,
      kind: 'endgame_position',
      fen: detail.fen,
      side_to_move: detail.side_to_move,
      mode: 'endgame_play_against_engine',
      result,
      played_move: playedMove,
      expected_move: expectedMove,
      time_spent_ms: Math.round(performance.now() - attemptStartRef.current),
      source_ref: detail.source_ref,
    };
    try {
      await endgameApi.postAttempt(payload);
    } catch (err) {
      console.error('[endgame] logAttempt failed', err);
    }
  }

  function handleMove(_detail: EndgameDetail, move: BoardMoveEvent): void {
    // Le reducer connaît déjà `detail` via le state — on dispatch sans
    // le ré-injecter (sinon on dupliquerait la source de vérité). _detail
    // reste dans la signature pour conserver le call-site de la prop onMove.
    // F-4 P1 : `promotion` propagé pour les finales R+P/promo (sans ce
    // champ, chess.js v1 throw et le coup est ignoré silencieusement).
    dispatch({
      type: 'MOVE_PLAYER',
      from: move.from,
      to: move.to,
      ...(move.promotion ? { promotion: move.promotion } : {}),
    });
  }

  // ─── Effet « coup adversaire » (T2 MVP2.5) ────────────────────────────
  // Identique au TacticsDrillPage : on dispatch `MOVE_ENGINE` via
  // `requestAnimationFrame` plutôt que setTimeout pour éviter le throttling
  // iOS Safari pendant qu'un touch event est consommé.
  useEffect(() => {
    if (state.kind !== 'awaiting_engine') return;
    const rafId = requestAnimationFrame(() => {
      dispatch({ type: 'MOVE_ENGINE' });
    });
    return () => cancelAnimationFrame(rafId);
  }, [state.kind]);

  // ─── Effets de bord post-transition (score, XP, log) ──────────────────
  // On observe les transitions sortantes :
  //  - chaque cursor incrémenté en `awaiting_player`/`awaiting_engine` après
  //    un coup correct = +15 XP local (animation visuelle)
  //  - `success` finale = log + award XP backend + score.ok++
  //  - `fail` = log + score.ko++
  // `lastBoundaryRef` empêche le double-trigger en StrictMode dev.
  useEffect(() => {
    if (state.kind === 'idle') return;
    const boundaryKey = `${state.detail.slug}:${state.kind}:${state.cursor}`;
    if (lastBoundaryRef.current === boundaryKey) return;

    if (state.kind === 'awaiting_engine' || state.kind === 'awaiting_player') {
      // Cursor > 0 et on vient d'arriver sur ce state : Léon vient de jouer
      // un coup correct (state cursor a été incrémenté côté reducer).
      // Cas particulier : MOVE_ENGINE produit aussi awaiting_player avec
      // cursor + 1 — on ne veut pas dégommer un coup XP supplémentaire.
      // Donc on n'incrémente le XP que sur les transitions issues d'un coup
      // joueur, identifiables par le fait que `awaiting_engine` est le
      // *premier* signal post-coup-joueur.
      if (state.kind === 'awaiting_engine' && state.cursor > 0) {
        lastBoundaryRef.current = boundaryKey;
        setSessionXp((x) => x + XP_PER_MOVE);
        setXpBurst({ value: XP_PER_MOVE, key: Date.now() });
      }
    } else if (state.kind === 'success') {
      lastBoundaryRef.current = boundaryKey;
      void logAttempt(
        state.detail,
        'success',
        state.playedMove,
        state.expectedMove,
      );
      setScore((s) => ({ ...s, ok: s.ok + 1 }));
      // Fix idempotence permanente 2026-05-17 : on n'incrémente l'XP du
      // coup final + XpFloater qu'APRÈS confirmation backend (`data.awarded
      // === true`). Sinon Léon rejoue une finale déjà résolue et voit le
      // burst +15 XP localement alors que le backend skip → divergence
      // visuelle au refresh (le compteur agrégé ne bougera pas). Backend
      // idempotence permanente sur (user, module, source_ref).
      //
      // Note : les XP intermédiaires des coups (branche awaiting_engine
      // ci-dessus) restent local-only — ils ne sont jamais persistés
      // backend, donc disparaissent au refresh. Cohérent : seule la
      // FINALE awardée persiste.
      awardXp.mutate(
        {
          module: 'endgame',
          source_ref: state.detail.slug,
          xp_amount: XP_PER_MOVE,
        },
        {
          onSuccess: (data) => {
            if (data.awarded) {
              setSessionXp((x) => x + XP_PER_MOVE);
              setXpBurst({ value: XP_PER_MOVE, key: Date.now() });
            }
          },
        },
      );
    } else if (state.kind === 'fail') {
      lastBoundaryRef.current = boundaryKey;
      void logAttempt(state.detail, 'fail', state.playedMove, state.expectedMove);
      setScore((s) => ({ ...s, ko: s.ko + 1 }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.kind, state.kind === 'idle' ? 0 : state.cursor]);

  // Le board attend `turnColor` au format `'white' | 'black'`. On le déduit
  // de la FEN (segment after-board) via Chess.js pour rester robuste si la
  // séquence inclut un coup adverse non-alternant (rare, mais possible avec
  // certaines finales `defend`).
  const turnFromFen = useMemo<'white' | 'black'>(() => {
    if (state.kind !== 'awaiting_player') return 'white';
    return new Chess(state.fen).turn() === 'w' ? 'white' : 'black';
  }, [state]);
  const currentTurnColor = turnFromFen;

  const legalMoves = useMemo(() => {
    if (state.kind !== 'awaiting_player') return new Map<Key, Key[]>();
    const map = new Map<Key, Key[]>();
    const chess = new Chess(state.fen);
    for (const m of chess.moves({ verbose: true })) {
      const list = map.get(m.from as Key) ?? [];
      list.push(m.to as Key);
      map.set(m.from as Key, list);
    }
    return map;
  }, [state]);

  const detail = detailQ.data;

  useEffect(() => {
    if (detail && state.kind === 'idle') start(detail);
  }, [detail, state.kind]);

  // ─── TurnIndicator (feature/turn-indicator) ────────────────────────────
  // Mapping reducer → state visuel. Le coup adversaire est rejoué via rAF
  // (cf. useEffect ci-dessus) donc `awaiting_engine` peut durer < 16ms.
  // `useMinDuration(rawState, 400)` force l'affichage minimum de 400ms pour
  // que Léon perçoive le clignotement « adversaire joue → à toi de jouer ».
  //
  // Le hook DOIT être appelé avant les early returns ci-dessous
  // (`detailQ.isPending`, `detailQ.isError`) — Rules of Hooks.
  const rawTurnState: TurnIndicatorState =
    state.kind === 'awaiting_player'
      ? 'user-turn'
      : state.kind === 'awaiting_engine'
        ? 'opponent-thinking'
        : 'idle';
  const turnState = useMinDuration<TurnIndicatorState>(rawTurnState, 400);

  if (detailQ.isPending) return <FrameMessage>Chargement de la finale…</FrameMessage>;
  if (detailQ.isError || !detail) return <FrameMessage>Finale introuvable.</FrameMessage>;

  const total = score.ok + score.ko;
  const successRate = total > 0 ? Math.round((score.ok / total) * 100) : 0;
  const cursor = state.kind !== 'idle' ? state.cursor : 0;
  // `inPlay` = position interactive ou en train d'animer le coup adversaire.
  const inPlay =
    state.kind === 'awaiting_player' || state.kind === 'awaiting_engine';

  const totalMoves = detail.key_moves.length;
  const outColor = OUTCOME_COLOR[detail.target_outcome];
  const outLabel = OUTCOME_LABEL[detail.target_outcome];

  const chips = [
    { label: 'Objectif', value: outLabel, color: outColor },
    { label: 'Coup', value: `${Math.min(cursor + 1, totalMoves)}/${totalMoves}`, color: 'var(--amber-500)' },
  ];

  return (
    <>
      <FixedNav chips={chips} />
      <main className="mx-auto max-w-[1280px] px-6 py-6 flex flex-col gap-5">
        {/* Header condensé */}
        <header className="flex flex-col gap-2">
          <div className="flex items-center gap-2 text-body-sm">
            <Link
              to="/endgame-forge"
              className="text-ink-300 hover:text-paper-50 transition-all duration-150"
            >
              ← Endgame Forge
            </Link>
            {detail.sub_theme && (
              <span className="font-mono text-[11px] text-ink-300">
                · {detail.sub_theme}
              </span>
            )}
          </div>
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <h1 className="font-display text-heading-1 text-paper-50">{detail.name}</h1>
              <div className="mt-1.5 flex items-center gap-3 flex-wrap">
                <span
                  className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border text-[11px] font-mono"
                  style={{ color: outColor, borderColor: outColor }}
                >
                  <span className="size-1.5 rounded-full" style={{ background: outColor }} aria-hidden="true" />
                  {outLabel}
                </span>
                <span className="font-mono text-xs text-ink-300">
                  eval initial{' '}
                  <span className="text-paper-50">{detail.eval_at_start ?? '—'}</span>
                  {detail.eval_engine ? ` · ${detail.eval_engine}` : ''}
                </span>
              </div>
            </div>
            <div className="flex items-center gap-4 font-mono text-xs">
              <div className="flex flex-col items-end gap-0.5">
                <Caption>Session</Caption>
                <SessionTimer startedAtMs={sessionStartRef.current} />
              </div>
              <span className="text-ink-700">·</span>
              <div className="flex flex-col items-end gap-0.5">
                <Caption>Réussite</Caption>
                <div className="flex items-center gap-1.5 text-sm">
                  <AnimatedNumber
                    value={successRate}
                    format={(v) => `${Math.round(v)}%`}
                    style={{
                      color: successRate >= 75 ? 'var(--volt-500)' : 'var(--amber-500)',
                      fontWeight: 500,
                    }}
                  />
                  <span className="text-ink-700">·</span>
                  <span className="text-volt-500">✓{score.ok}</span>
                  <span className="text-coral-500">✗{score.ko}</span>
                </div>
              </div>
              <span className="text-ink-700">·</span>
              <div className="flex flex-col items-end gap-0.5">
                <Caption>XP session</Caption>
                <span className="font-mono text-sm text-acid-500 font-medium tabular-nums">
                  +{sessionXp} pts
                </span>
              </div>
            </div>
          </div>
        </header>

        <KidHint tone="amber" title="Pourquoi ce drill ?">
          Une finale technique se convertit avec précision, pas avec talent. Tu
          joues chaque coup correct prouvé par Stockfish jusqu'à transformer
          l'avantage en victoire.
        </KidHint>

        {/* Le Board ne doit pas monter tant que `state.kind === 'idle'` :
            Chessground s'initialise alors avec `viewOnly: true` et ne ré-applique
            jamais la classe `manipulable` sur son wrapper même après
            `api.set({ viewOnly: false })`. Résultat : board affiché mais
            drag/click silencieux. Pattern aligné sur TacticsDrillPage qui
            n'a pas le bug grâce au même garde-fou `{currentPuzzle && ...}`. */}
        {state.kind !== 'idle' && (
        <section className="grid grid-cols-1 lg:grid-cols-[minmax(280px,360px)_1fr] gap-6 items-start">
          {/* LEFT — info & sequence */}
          <aside className="flex flex-col gap-4">
            <div className="grid grid-cols-2 gap-3">
              <CursorTile cursor={cursor} total={totalMoves} />
              <EvalTile evalText={detail.eval_at_start} engine={detail.eval_engine} />
            </div>

            <GlassCard accent="volt" icon="✓" label="Objectif">
              <p className="text-body-sm text-paper-50 leading-relaxed">
                <strong style={{ color: outColor }}>{outLabel}</strong> avec les{' '}
                {detail.side_to_move === 'white' ? 'Blancs' : 'Noirs'}.
              </p>
              {detail.target_outcome === 'draw' && (
                <p className="mt-1.5 text-xs text-ink-300 leading-relaxed">
                  Tu défends — pas de perte, pas besoin de gagner.
                </p>
              )}
            </GlassCard>

            <GlassCard
              accent="violet"
              icon="➳"
              label={`Séquence prouvée · ${totalMoves} coups`}
            >
              <div className="flex items-center gap-1.5 flex-wrap">
                {detail.key_moves.map((m, i) => {
                  const isCurrent = inPlay && i === cursor;
                  const isHidden = inPlay && i > cursor;
                  return (
                    <span key={i} className="contents">
                      {i > 0 && (
                        <span className="text-ink-300 font-mono text-xs">→</span>
                      )}
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded-md border font-mono text-xs tabular-nums ${
                          isCurrent
                            ? 'border-amber-500 bg-amber-500/15 text-paper-50'
                            : isHidden
                              ? 'border-ink-700 bg-ink-900 text-ink-300'
                              : 'border-violet-500/40 bg-violet-500/10 text-paper-50'
                        }`}
                      >
                        {isHidden ? '?' : m}
                      </span>
                    </span>
                  );
                })}
              </div>
            </GlassCard>

            <GlassCard accent="amber" icon="📚" label="Source">
              <p className="font-mono text-xs text-amber-500" style={{ fontWeight: 500 }}>
                {ENDGAME_SOURCE_KIND_LABEL_FR[detail.source_kind] ?? detail.source_kind}
              </p>
              <p className="mt-1.5 font-mono text-xs text-ink-300 leading-relaxed">
                {detail.source_ref}
              </p>
            </GlassCard>

            {(state.kind === 'success' || state.kind === 'fail') && (
              <GlassCard
                accent={state.kind === 'success' ? 'volt' : 'coral'}
                icon={state.kind === 'success' ? '🎯' : '⚡'}
                label={
                  state.kind === 'success'
                    ? 'Convertie'
                    : `Manquée — attendu ${state.expectedMove}`
                }
              >
                <p className="text-body-sm text-paper-50 leading-relaxed">
                  {detail.explanation}
                </p>
                {state.kind === 'fail' && (
                  <p className="mt-2 font-mono text-[11px] uppercase tracking-wider text-coral-500">
                    Carte de révision créée · due demain
                  </p>
                )}
              </GlassCard>
            )}
          </aside>

          {/* RIGHT — board */}
          <div className="flex flex-col gap-3 relative">
            {xpBurst && (
              <XpFloater
                value={xpBurst.value}
                tokenKey={xpBurst.key}
                caption="Coup de finale correct"
                className="top-0 right-4"
              />
            )}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <PulseDot
                  color={
                    state.kind === 'success'
                      ? 'var(--volt-500)'
                      : state.kind === 'fail'
                        ? 'var(--coral-500)'
                        : 'var(--amber-500)'
                  }
                  size={7}
                />
                <Caption>
                  {state.kind === 'success'
                    ? 'Convertie'
                    : state.kind === 'fail'
                      ? 'Manquée'
                      : state.kind === 'awaiting_engine'
                        ? 'Adversaire joue…'
                        : `Coup ${cursor + 1}`}
                </Caption>
              </div>
              <PositionTimer
                startedAtMs={positionStartRef.current}
                active={state.kind === 'awaiting_player'}
              />
            </div>

            <TurnIndicator
              state={turnState}
              idleLabel={
                state.kind === 'success'
                  ? 'Finale convertie'
                  : state.kind === 'fail'
                    ? 'Finale manquée'
                    : 'En attente'
              }
            />

            <Board
              fen={inPlay ? state.fen : detail.fen}
              orientation={detail.side_to_move}
              interactive={state.kind === 'awaiting_player'}
              engineThinking={state.kind === 'awaiting_engine'}
              {...(state.kind === 'awaiting_player'
                ? { legalMoves, turnColor: currentTurnColor }
                : {})}
              {...(inPlay && state.lastMove ? { lastMove: state.lastMove } : {})}
              onMove={(m) => handleMove(detail, m)}
            />

            {state.kind === 'awaiting_player' && (
              <div className="flex items-center gap-3 flex-wrap">
                <span className="ml-auto font-mono text-[11px] text-ink-300">
                  glisser · cliquer · clavier{' '}
                  <span className="px-1.5 py-0.5 rounded border border-ink-700 bg-ink-900 text-paper-50 ml-1">
                    e2e4 ↵
                  </span>
                </span>
              </div>
            )}
            {(state.kind === 'success' || state.kind === 'fail') && (
              <div className="flex items-center gap-3 flex-wrap">
                <Button variant="primary" accent="amber" onClick={() => start(detail)}>
                  Recommencer
                </Button>
                <Button variant="ghost" onClick={() => navigate('/endgame-forge')}>
                  Toutes les finales
                </Button>
              </div>
            )}
          </div>
        </section>
        )}
      </main>
    </>
  );
}

// ─── Sous-composants ──────────────────────────────────────────────────────

function CursorTile({ cursor, total }: { cursor: number; total: number }): JSX.Element {
  const pct = total > 0 ? Math.round(((cursor + 1) / total) * 100) : 0;
  return (
    <div className="flex flex-col gap-1.5 p-3 rounded-lg border border-ink-700 glass">
      <Caption>Coup</Caption>
      <div className="flex items-baseline gap-2">
        <span className="font-mono text-paper-50 leading-none" style={{ fontSize: 22, fontWeight: 500 }}>
          {Math.min(cursor + 1, total)}
        </span>
        <span className="font-mono text-xs text-ink-300">/ {total}</span>
      </div>
      <div className="h-1 bg-ink-700 rounded-full overflow-hidden">
        <div
          className="h-full bg-amber-500 transition-all duration-300"
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

function EvalTile({
  evalText,
  engine,
}: {
  evalText: string | null;
  engine: string | null;
}): JSX.Element {
  return (
    <div className="flex flex-col gap-1.5 p-3 rounded-lg border border-ink-700 glass">
      <Caption>Éval. initiale</Caption>
      <span className="font-mono text-amber-500 leading-none" style={{ fontSize: 18, fontWeight: 500 }}>
        {evalText ?? '—'}
      </span>
      {engine && (
        <span className="font-mono text-[11px] text-ink-300">{engine}</span>
      )}
    </div>
  );
}

function GlassCard({
  accent,
  icon,
  label,
  children,
}: {
  accent: 'amber' | 'violet' | 'volt' | 'coral';
  icon: string;
  label: string;
  children: ReactNode;
}): JSX.Element {
  const accentColor = {
    amber: 'var(--amber-500)',
    violet: 'var(--violet-500)',
    volt: 'var(--volt-500)',
    coral: 'var(--coral-500)',
  }[accent];
  // String littéral (pas template) : Tailwind purge supprime les classes
  // construites via `glass-${accent}` faute de pouvoir les détecter.
  const glassClass = {
    amber: 'glass glass-amber',
    violet: 'glass glass-violet',
    volt: 'glass glass-volt',
    coral: 'glass glass-coral',
  }[accent];
  return (
    <div className={`relative p-4 rounded-lg ${glassClass}`}>
      <span
        aria-hidden="true"
        className="absolute top-3 right-3 inline-flex items-center justify-center w-6 h-6 rounded border font-mono text-xs"
        style={{ color: accentColor, borderColor: `${accentColor}33`, backgroundColor: `${accentColor}10` }}
      >
        {icon}
      </span>
      <div className="mr-9">
        <span
          className="block text-caption uppercase mb-2"
          style={{ color: accentColor, letterSpacing: '0.05em', fontWeight: 500 }}
        >
          {label}
        </span>
        {children}
      </div>
    </div>
  );
}

function PositionTimer({
  startedAtMs,
  active,
}: {
  startedAtMs: number;
  active: boolean;
}): JSX.Element {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!active) return;
    const id = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(id);
  }, [active]);
  const elapsed = Math.max(0, (now - startedAtMs) / 1000);
  return (
    <span
      className="font-mono text-[11px] tabular-nums whitespace-nowrap"
      style={{ color: elapsed > 30 ? 'var(--amber-500)' : 'var(--ink-300)' }}
    >
      {elapsed.toFixed(1)}s
    </span>
  );
}

function FrameMessage({ children }: { children: ReactNode }): JSX.Element {
  return (
    <>
      <FixedNav chips={[]} />
      <main className="mx-auto max-w-[1280px] px-6 py-8 flex flex-col gap-4">
        <Link
          to="/endgame-forge"
          className="text-caption uppercase text-ink-300 hover:text-paper-50 transition-all duration-150"
        >
          ← Toutes les finales
        </Link>
        <p className="text-ink-300 italic">{children}</p>
      </main>
    </>
  );
}
